import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';
import { Trash2, Plus, Key, Copy, Check, AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';

interface WebhookApiKey {
  id: string;
  name: string;
  keyPrefix: string;
  isActive: boolean;
  createdAt: string;
}

const WebhookApiKeys: React.FC = () => {
  const { user } = useAuth();
  const [apiKeys, setApiKeys] = useState<WebhookApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [showGeneratedKey, setShowGeneratedKey] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);

  const fetchApiKeys = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/webhook/api-keys');
      setApiKeys(response.data);
    } catch (error) {
      console.error('Error fetching API keys:', error);
    } finally {
      setLoading(false);
    }
  };

  const createApiKey = async () => {
    if (!newKeyName.trim()) return;

    try {
      setCreating(true);
      const response = await api.post('/admin/webhook/api-keys', {
        name: newKeyName.trim(),
      });

      const data = response.data;
      setGeneratedKey(data.fullKey);
      setShowGeneratedKey(true);
      setNewKeyName('');

      // Refresh the list
      await fetchApiKeys();
    } catch (error) {
      console.error('Error creating API key:', error);
      alert('Error creating API key');
    } finally {
      setCreating(false);
    }
  };

  const deleteApiKey = async (id: string) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Are you sure you want to delete this API key?')) {
      return;
    }

    try {
      await api.delete(`/admin/webhook/api-keys/${id}`);
      await fetchApiKeys();
    } catch (error) {
      console.error('Error deleting API key:', error);
      alert('Error deleting API key');
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
    }
  };

  useEffect(() => {
    if (user?.role === 'super-admin') {
      fetchApiKeys();
    }
  }, [user]);

  if (user?.role !== 'super-admin') {
    return (
      <div className='flex justify-center items-center py-16'>
        <Card>
          <CardContent className='text-center py-8'>
            <AlertTriangle className='w-12 h-12 text-yellow-500 mx-auto mb-4' />
            <h2 className='text-xl font-semibold mb-2'>Access Denied</h2>
            <p className='text-gray-600'>
              Only super administrators can access this section.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className='space-y-4 sm:space-y-6'>
      <div className='flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4'>
        <div>
          <h1 className='text-2xl sm:text-3xl font-bold text-gray-900'>
            Webhook Configuration
          </h1>
          <p className='text-sm sm:text-base text-gray-600'>
            Manage API keys for webhook authentication
          </p>
        </div>
      </div>

      {/* Modal for showing generated key */}
      {showGeneratedKey && generatedKey && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
          <Card className='w-full max-w-md'>
            <CardHeader>
              <CardTitle className='text-green-600'>
                <Check className='w-5 h-5 inline mr-2' />
                API Key Created
              </CardTitle>
              <CardDescription>
                Save this key securely. You won't be able to see it again.
              </CardDescription>
            </CardHeader>
            <CardContent className='space-y-4'>
              <div className='space-y-2'>
                <label className='text-sm font-medium'>Complete API Key:</label>
                <div className='flex items-center space-x-2'>
                  <Input
                    value={generatedKey}
                    readOnly
                    className='font-mono text-sm'
                  />
                  <Button
                    size='sm'
                    variant='outline'
                    onClick={() => copyToClipboard(generatedKey)}
                  >
                    {copiedKey ? (
                      <Check className='w-4 h-4' />
                    ) : (
                      <Copy className='w-4 h-4' />
                    )}
                  </Button>
                </div>
              </div>
              <div className='flex justify-end space-x-2'>
                <Button
                  onClick={() => {
                    setShowGeneratedKey(false);
                    setGeneratedKey(null);
                  }}
                >
                  Close
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Create new API key */}
      <Card>
        <CardHeader>
          <CardTitle>
            <Plus className='w-5 h-5 inline mr-2' />
            Create New API Key
          </CardTitle>
          <CardDescription>
            Create a new API key to authenticate webhooks
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className='flex items-center space-x-2'>
            <Input
              placeholder='API key name'
              value={newKeyName}
              onChange={e => setNewKeyName(e.target.value)}
              onKeyPress={e => e.key === 'Enter' && !creating && createApiKey()}
            />
            <Button
              onClick={createApiKey}
              disabled={creating || !newKeyName.trim()}
            >
              {creating ? 'Creating...' : 'Create'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* API Keys list */}
      <Card>
        <CardHeader>
          <CardTitle>
            <Key className='w-5 h-5 inline mr-2' />
            Existing API Keys
          </CardTitle>
          <CardDescription>List of all active API keys</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className='text-center py-8'>
              <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto'></div>
              <p className='text-gray-600 mt-2'>Loading API keys...</p>
            </div>
          ) : apiKeys.length === 0 ? (
            <div className='text-center py-8'>
              <Key className='w-12 h-12 text-gray-400 mx-auto mb-4' />
              <p className='text-gray-600'>No API keys configured</p>
            </div>
          ) : (
            <div className='space-y-4'>
              {apiKeys.map(apiKey => (
                <div
                  key={apiKey.id}
                  className='flex items-center justify-between p-4 border rounded-lg'
                >
                  <div className='flex-1'>
                    <h3 className='font-medium'>{apiKey.name}</h3>
                    <p className='text-sm text-gray-600'>
                      Prefix: {apiKey.keyPrefix}***
                    </p>
                    <p className='text-xs text-gray-500'>
                      Created: {new Date(apiKey.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className='flex items-center space-x-2'>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        apiKey.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {apiKey.isActive ? 'Active' : 'Inactive'}
                    </span>
                    <Button
                      size='sm'
                      variant='destructive'
                      onClick={() => deleteApiKey(apiKey.id)}
                    >
                      <Trash2 className='w-4 h-4' />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default WebhookApiKeys;
